/*
 * Zustand des HMI-Editors: Entwurf, Auswahl, Rueckgaengig, Speichern.
 *
 * Eine Fabrik und kein modulweiter Zustand. Der Unterschied zaehlt: ein
 * modulweiter Store ueberlebt den Seitenwechsel, und ein Editor, der beim
 * zweiten Oeffnen noch den Entwurf der vorigen Anlage haelt, zeigt dem Autor
 * fremde Arbeit als seine eigene. Ein Store gehoert zu einer Bearbeitung.
 *
 * ZUSTAENDE STATT GEGENOPERATIONEN. Rueckgaengig ist ein Stapel aus
 * vollstaendigen Projekten. Der teurere Weg waere ein Stapel aus
 * Gegenoperationen, und er waere nur so gut wie die jeweils zugehoerige
 * Umkehrfunktion. Bei zweiundzwanzig Widget-Typen mit eigenen Eigenschaften
 * hiesse das zweiundzwanzig Gelegenheiten, eine Umkehrung falsch zu schreiben,
 * und der Fehler zeigt sich erst, wenn jemand Arbeit verliert. Ein Projekt ist
 * ein paar Kilobyte JSON; hundert davon im Speicher sind billiger als eine
 * einzige falsche Umkehrung.
 */

import { computed, signal, type ReadonlySignal, type Signal } from '@preact/signals';

import { ApiError } from '../../api/client';
import {
  serialiseProjectBody,
  updateHmiProject,
  type HmiProjectDetail,
} from '../projectApi';
import { collectBindingKeys } from '../bindingKey';
import type { HmiProject, LayoutTarget, WidgetPlacement } from '../widgets';
import {
  LAYOUT_TARGET_ORDER,
  blockingFindings,
  validateProject,
  type ValidationFinding,
} from './editorModel';

/** Wie viele Schritte zurueck. Hundert Schritte sind mehr, als ein Mensch in
 *  einer Sitzung zurueckgehen will, und kosten bei der Groesse eines
 *  Projekts nichts, was auffiele. */
const HISTORY_LIMIT = 100;

/**
 * Ergebnis eines Speicherversuchs.
 *
 * Der Konflikt ist ein eigener Fall und keine Fehlermeldung. Wenn zwei Leute
 * dasselbe Bild bearbeitet haben, ist die einzige ehrliche Antwort, beides zu
 * zeigen und den Autor entscheiden zu lassen. Ein automatisches
 * Zusammenfuehren waere bei einer Anordnung von Hand geraten, und ein
 * stilles Ueberschreiben nimmt jemandem die Arbeit weg, ohne ihn zu fragen.
 */
export type SaveResult =
  | { status: 'saved'; revision: number }
  | { status: 'blocked'; findings: ValidationFinding[] }
  | { status: 'conflict'; conflict: RevisionConflict }
  | { status: 'failed'; message: string };

/**
 * Der Rumpf, den der Server bei HTTP 409 schickt.
 *
 * Zeichengleich zu <c>HmiProjectRevisionConflictDto</c> auf der Serverseite.
 * Dass hier nicht nur eine Nummer steht, sondern auch wer wann gespeichert
 * hat, ist der Unterschied zwischen "Konflikt" und einer Meldung, mit der ein
 * Autor etwas anfangen kann: er kann den Kollegen fragen, statt zu raten.
 * Alle Felder sind optional gelesen, weil ein aelterer Server nur einen Teil
 * schicken koennte und eine fehlende Angabe kein Grund ist, den Konflikt
 * selbst zu verschlucken.
 */
export interface RevisionConflict {
  error?: string;
  submittedRevision?: number;
  currentRevision?: number;
  updatedAt?: string;
  updatedBy?: string | null;
}

/**
 * Eine uebernommene Anordnung, die noch nicht im Projekt steht.
 *
 * Warum sie NEBEN dem Entwurf liegt und nicht darin. Eine von einem anderen
 * Ziel uebernommene Anordnung ist ein Ausgangspunkt und kein Ergebnis: sie
 * kommt aus einer Rechnung und nicht aus einer Entscheidung. Stuende sie
 * sofort im Projekt, waere sie von gemachter Arbeit nicht mehr zu
 * unterscheiden, der Autor wuerde sie nicht mehr pruefen, und beim naechsten
 * Speichern ginge sie so auf das Geraet. Also derselbe Weg wie bei der
 * Anfangsanordnung aus `autoFlowPlacements`: zeigen, kennzeichnen,
 * festschreiben erst, wenn der Autor bestaetigt oder etwas anfasst.
 *
 * `screenId` und `target` gehoeren dazu, weil ein Vorschlag genau fuer EIN
 * Bild und EIN Ziel gilt. Ohne sie zeigte er nach einem Wechsel dieselben
 * Zahlen auf einer fremden Flaeche.
 */
export interface LayoutProposal {
  screenId: string;
  target: LayoutTarget;
  /** Das Ziel, von dem uebernommen wurde. Steht in der Meldung. */
  from: LayoutTarget;
  placements: Record<string, WidgetPlacement>;
  /** Widgets, die auf dem Zielraster keinen Platz fanden. */
  overflow: string[];
  /** Treu uebernommen oder in Lesereihenfolge neu umgebrochen. */
  mode: 'scaled' | 'reflowed';
}

export interface EditorStore {
  /** Der Entwurf. Nur ueber {@link apply} aendern, sonst faellt er aus der
   *  Rueckgaengig-Kette. */
  readonly project: ReadonlySignal<HmiProject>;
  /**
   * Die ausgewaehlten Widgets, in der Reihenfolge, in der sie gewaehlt
   * wurden.
   *
   * <b>Warum eine Liste und kein Set, und warum die Reihenfolge zaehlt.</b>
   * Ausrichten und Angleichen brauchen einen Bezug, und der Bezug ist das
   * ZUERST gewaehlte Widget (siehe `alignPlacements`). Ein Set haette keine
   * Reihenfolge, und ein aus der Lage abgeleiteter Bezug waere bei jeder
   * zweiten Anwendung ein anderer.
   *
   * <b>Warum die Einzelauswahl zur Liste wurde und nicht daneben steht.</b>
   * Zwei Signale, eines fuer "das Widget" und eines fuer "die Widgets", waeren
   * zwei Wahrheiten ueber dieselbe Frage; sie laufen beim ersten Rueckgaengig
   * auseinander. Wer nur eines braucht, nimmt {@link primarySelection}.
   */
  readonly selection: Signal<readonly string[]>;
  /**
   * Das fuehrende Widget der Auswahl, oder null.
   *
   * Fuer alles, was genau eines meint: das Eigenschaftsfeld und der Bezug
   * beim Ausrichten.
   */
  readonly primarySelection: ReadonlySignal<string | null>;
  /** Id des angezeigten Bildes. */
  readonly activeScreenId: Signal<string>;
  /** Layout-Ziel, das gerade bearbeitet wird. */
  readonly activeTarget: Signal<LayoutTarget>;
  /** Eine uebernommene Anordnung, solange sie nicht festgeschrieben ist. */
  readonly proposal: Signal<LayoutProposal | null>;

  readonly dirty: ReadonlySignal<boolean>;
  readonly canUndo: ReadonlySignal<boolean>;
  readonly canRedo: ReadonlySignal<boolean>;
  readonly findings: ReadonlySignal<ValidationFinding[]>;
  readonly saving: ReadonlySignal<boolean>;
  /** Stand, den der Server zuletzt bestaetigt hat. */
  readonly revision: ReadonlySignal<number>;

  /**
   * Neuen Entwurf uebernehmen und einen Schritt in die Rueckgaengig-Kette
   * legen.
   *
   * `coalesceKey` fasst aufeinanderfolgende Aenderungen derselben Herkunft zu
   * einem Schritt zusammen. Ohne das waere jeder Tastendruck in einem
   * Zahlenfeld ein eigener Schritt, und Rueckgaengig braeuchte
   * siebzehn Klicks, um eine Eingabe zurueckzunehmen.
   */
  apply(next: HmiProject, coalesceKey?: string): void;

  undo(): void;
  redo(): void;

  /** Entwurf gegen den Server schreiben. */
  save(): Promise<SaveResult>;

  /**
   * Den Entwurf durch den Serverstand ersetzen und die Historie verwerfen.
   * Der Weg aus einem Konflikt, wenn der Autor die fremde Fassung nimmt.
   */
  resetTo(detail: HmiProjectDetail, project: HmiProject): void;
}

interface Options {
  projectId: string;
  /** Stand, mit dem geladen wurde. */
  revision: number;
  /** Anfangsentwurf, bereits ausgepackt. */
  project: HmiProject;
  /** Name und Beschreibung reisen neben dem Rumpf und werden mitgeschickt,
   *  damit ein Umbenennen im Editor nicht verloren geht. */
  name: string;
  description?: string | null;
  /**
   * Felder des Rumpfs, die der Editor nicht bearbeitet.
   *
   * Werden unveraendert wieder mitgeschrieben. Ohne sie verloere jeder
   * Speichervorgang still alles, was der Editor nicht kennt, zum Beispiel
   * `schematicAssets`. Siehe `parseProjectBody`.
   */
  rest?: Record<string, unknown>;
  /**
   * Der Rumpf, wie er auf dem Server liegt.
   *
   * Vergleichsgrundlage fuer "nicht gespeichert". Ohne sie waere der
   * Anfangsentwurf per Definition sauber, und genau das ist falsch, sobald die
   * Seite etwas beilegt, das der Server nicht hat: ein frisch angelegtes
   * Projekt bekommt beim Oeffnen ein erstes Bild, damit der Autor eine Flaeche
   * hat. Ohne diese Angabe stuende der Editor dann auf "Gespeichert", und ein
   * geschlossener Reiter haette das Bild lautlos verworfen.
   */
  savedBodyJson?: string;
}

export function createEditorStore(options: Options): EditorStore {
  const project = signal<HmiProject>(options.project);
  const selection = signal<readonly string[]>([]);
  const primarySelection = computed(() => selection.value[0] ?? null);
  const activeScreenId = signal<string>(options.project.screens[0]?.id ?? '');
  const activeTarget = signal<LayoutTarget>(LAYOUT_TARGET_ORDER[0]);
  const proposal = signal<LayoutProposal | null>(null);

  const revision = signal<number>(options.revision);
  const saving = signal<boolean>(false);

  // Der zuletzt bestaetigte Stand als JSON. Der Vergleich laeuft ueber die
  // serialisierte Form und nicht ueber Objektgleichheit, weil genau diese
  // Form auf die Reise geht: was gleich serialisiert, ist fuer den Server
  // dasselbe Projekt, auch wenn die Objekte verschieden sind.
  const rest = options.rest ?? {};
  const savedJson = signal<string>(options.savedBodyJson ?? bodyOf(options.project, rest));

  let undoStack: HmiProject[] = [];
  let redoStack: HmiProject[] = [];
  let lastCoalesceKey: string | null = null;
  const historyTick = signal(0);

  const dirty = computed(() => bodyOf(project.value, rest) !== savedJson.value);
  const canUndo = computed(() => {
    historyTick.value;
    return undoStack.length > 0;
  });
  const canRedo = computed(() => {
    historyTick.value;
    return redoStack.length > 0;
  });
  // Der Sammler wird hier hereingereicht und nicht in `editorModel` importiert;
  // die Begruendung steht an `validateProject`. Diese Datei darf ihn
  // importieren, sie wird nicht vom Node-Testlaeufer geladen.
  const findings = computed(() => validateProject(project.value, collectBindingKeys));

  function apply(next: HmiProject, coalesceKey?: string): void {
    const sameRun = coalesceKey !== undefined && coalesceKey === lastCoalesceKey;
    if (!sameRun) {
      undoStack.push(project.value);
      if (undoStack.length > HISTORY_LIMIT) undoStack = undoStack.slice(-HISTORY_LIMIT);
    }
    lastCoalesceKey = coalesceKey ?? null;
    redoStack = [];
    project.value = next;
    historyTick.value += 1;
    // Ein Vorschlag bezieht sich auf den Stand, aus dem er gerechnet wurde.
    // Sobald das Projekt sich aendert, ist er entweder gerade festgeschrieben
    // worden oder veraltet; beide Male hat er ausgedient. Stehen bliebe er
    // sonst als gestrichelte Anordnung ueber einer, die es schon gibt.
    proposal.value = null;
    ensureSelectionAndScreen();
  }

  function undo(): void {
    const previous = undoStack.pop();
    if (!previous) return;
    redoStack.push(project.value);
    project.value = previous;
    lastCoalesceKey = null;
    historyTick.value += 1;
    proposal.value = null;
    ensureSelectionAndScreen();
  }

  function redo(): void {
    const next = redoStack.pop();
    if (!next) return;
    undoStack.push(project.value);
    project.value = next;
    lastCoalesceKey = null;
    historyTick.value += 1;
    proposal.value = null;
    ensureSelectionAndScreen();
  }

  /**
   * Auswahl und angezeigtes Bild gegen den Entwurf pruefen.
   *
   * Nach einem Rueckgaengig kann das ausgewaehlte Widget oder das angezeigte
   * Bild nicht mehr existieren. Eine Auswahl, die ins Leere zeigt, laesst das
   * Eigenschaftsfeld leer stehen, und der Autor haelt den Editor fuer kaputt.
   *
   * Bei einer Mehrfachauswahl wird gefiltert und nicht geleert: wer sechs
   * Kacheln gewaehlt und eine davon geloescht hat, will die anderen fuenf
   * behalten. Die Reihenfolge bleibt dabei erhalten, samt dem fuehrenden
   * Widget, solange dieses selbst noch da ist. Das neue Signal wird nur
   * gesetzt, wenn sich wirklich etwas aendert, sonst stiesse jede Aenderung am
   * Projekt eine Neuberechnung aller Auswahl-Abhaengigen an.
   */
  function ensureSelectionAndScreen(): void {
    const current = project.value;

    if (!current.screens.some((s) => s.id === activeScreenId.value)) {
      activeScreenId.value = current.screens[0]?.id ?? '';
    }

    const known = new Set(current.widgets.map((w) => w.id));
    const kept = selection.value.filter((id) => known.has(id));
    if (kept.length !== selection.value.length) selection.value = kept;
  }

  async function save(): Promise<SaveResult> {
    const blocking = blockingFindings(findings.value);
    if (blocking.length > 0) {
      return { status: 'blocked', findings: blocking };
    }

    saving.value = true;
    try {
      const body = bodyOf(project.value, rest);
      const detail = await updateHmiProject(options.projectId, {
        name: options.name,
        description: options.description ?? undefined,
        bodyJson: body,
        revision: revision.value,
      });

      revision.value = detail.revision;
      savedJson.value = body;
      return { status: 'saved', revision: detail.revision };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Der Namenskonflikt (zwei Projekte gleichen Namens in derselben
        // Anlage) kommt ebenfalls als 409, traegt aber keine Revision. Beide
        // als Revisionskonflikt zu behandeln waere falsch: der eine heilt
        // durch Neuladen, der andere durch Umbenennen.
        const body = (err.body ?? {}) as RevisionConflict;
        if (typeof body.currentRevision === 'number') {
          return { status: 'conflict', conflict: body };
        }
        return { status: 'failed', message: body.error ?? err.message };
      }
      return { status: 'failed', message: err instanceof Error ? err.message : String(err) };
    } finally {
      saving.value = false;
    }
  }

  function resetTo(detail: HmiProjectDetail, next: HmiProject): void {
    undoStack = [];
    redoStack = [];
    lastCoalesceKey = null;
    project.value = next;
    revision.value = detail.revision;
    savedJson.value = bodyOf(next, rest);
    historyTick.value += 1;
    ensureSelectionAndScreen();
  }

  return {
    project,
    selection,
    primarySelection,
    activeScreenId,
    activeTarget,
    proposal,
    dirty,
    canUndo,
    canRedo,
    findings,
    saving,
    revision,
    apply,
    undo,
    redo,
    save,
    resetTo,
  };
}

/** Der Rumpf, wie er auf die Reise geht. Eine Stelle, damit Vergleich und
 *  Versand nie auseinanderlaufen koennen. */
function bodyOf(project: HmiProject, rest: Record<string, unknown>): string {
  return serialiseProjectBody(project.widgets, project.screens, rest, project.appearance, project.notes);
}
